import {
  MouseEvent as ReactMouseEvent,
  RefObject,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { useEvent, useRafState, useToggle } from 'react-use';

interface UseResizableRightPanelOptions {
  containerRef: RefObject<HTMLDivElement | null>;
  defaultWidth: number;
  /** Design cap on the panel width, applied on top of whatever the container
   *  can spare. Omit to let the container alone bound the panel. */
  maxWidth?: number;
  /** Width the rest of the container must keep when deriving the max panel
   *  width from the container size. */
  minRemainingWidth?: number;
  minWidth: number;
  reservedWidth?: number;
  /**
   * Distance from the container's right edge to the panel's right edge, for
   * panels that don't sit flush against it — e.g. the chat side content pane,
   * whose right edge is the left edge of the chat transcript docked beside it.
   */
  rightOffset?: number;
}

const FALLBACK_MAX_WIDTH = 1200;

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

interface ResolveMaxPanelWidthOptions {
  /** Measured container width; pass 0 when the container isn't measured yet. */
  containerWidth: number;
  maxWidth?: number;
  minRemainingWidth: number;
  minWidth: number;
  reservedWidth: number;
  rightOffset: number;
}

/**
 * Max width the panel may occupy: whatever the container can spare after the
 * offsets, floored at minWidth and additionally capped by the explicit
 * maxWidth when provided. An unmeasured container gets the explicit cap (or a
 * generous fallback) so the panel isn't clamped down before the first layout
 * pass.
 */
export function resolveMaxPanelWidth({
  containerWidth,
  maxWidth,
  minRemainingWidth,
  minWidth,
  reservedWidth,
  rightOffset,
}: ResolveMaxPanelWidthOptions): number {
  if (containerWidth <= 0) {
    return maxWidth ?? FALLBACK_MAX_WIDTH;
  }

  const spareWidth = Math.max(
    minWidth,
    containerWidth - rightOffset - minRemainingWidth - reservedWidth
  );

  if (maxWidth !== undefined) {
    return Math.min(maxWidth, spareWidth);
  }

  return spareWidth;
}

export function useResizableRightPanel({
  containerRef,
  defaultWidth,
  maxWidth: maxWidthProp,
  minRemainingWidth = 0,
  minWidth,
  reservedWidth = 0,
  rightOffset = 0,
}: UseResizableRightPanelOptions) {
  const resolveMax = useCallback(
    () =>
      resolveMaxPanelWidth({
        containerWidth: containerRef.current?.clientWidth ?? 0,
        maxWidth: maxWidthProp,
        minRemainingWidth,
        minWidth,
        reservedWidth,
        rightOffset,
      }),
    [
      containerRef,
      maxWidthProp,
      minRemainingWidth,
      minWidth,
      reservedWidth,
      rightOffset,
    ]
  );

  const clampPanelWidth = useCallback(
    (nextWidth: number) => clamp(nextWidth, minWidth, resolveMax()),
    [minWidth, resolveMax]
  );

  const [panelWidth, setPanelWidth] = useRafState(() =>
    clampPanelWidth(defaultWidth)
  );
  const [isResizing, toggleResizing] = useToggle(false);
  const resizeRef = useRef<HTMLDivElement>(null);
  const hasInitialized = useRef(false);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    const syncPanelWidth = () => {
      const containerWidth = container.clientWidth;
      if (containerWidth <= 0) {
        return;
      }

      if (!hasInitialized.current) {
        hasInitialized.current = true;
        setPanelWidth(clampPanelWidth(defaultWidth));
        return;
      }

      setPanelWidth((previousWidth) => clampPanelWidth(previousWidth));
    };

    syncPanelWidth();

    const observer = new ResizeObserver(syncPanelWidth);
    observer.observe(container);

    return () => {
      observer.disconnect();
    };
  }, [clampPanelWidth, containerRef, defaultWidth, setPanelWidth]);

  const handleMouseDown = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>) => {
      event.preventDefault();
      toggleResizing(true);
    },
    [toggleResizing]
  );

  useEvent(
    'mousemove',
    (event: Event) => {
      if (!isResizing) {
        return;
      }

      const mouseEvent = event as MouseEvent;
      const containerRight =
        containerRef.current?.getBoundingClientRect().right ??
        window.innerWidth;
      setPanelWidth(
        clampPanelWidth(containerRight - rightOffset - mouseEvent.clientX)
      );
    },
    document
  );

  useEvent(
    'mouseup',
    () => {
      if (isResizing) {
        toggleResizing(false);
      }
    },
    document
  );

  useEffect(() => {
    if (!isResizing) {
      return;
    }

    const previousCursor = document.body.style.cursor;
    const previousUserSelect = document.body.style.userSelect;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    return () => {
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousUserSelect;
    };
  }, [isResizing]);

  return {
    handleMouseDown,
    isResizing,
    panelWidth,
    resizeRef,
  };
}
